// Effective-hours resolution for appointments (implementation plan §3.3).
//
// A resource's bookable window on a given date is resolved from four sources.
// First match wins, with one exception (see "Business closure" below):
//
//   1. resource shift    — a `businesses/{id}/shifts` doc carrying this `resourceId`
//   2. resource schedule — the resource's own weekly hours (`resource.schedule`)
//   3. business shift    — a shift doc with no `resourceId` (the legacy shape)
//   4. business hours    — `settings.reservations.hours[dayIndex]`
//
// **Business closure exception**: a business-wide shift with `closed === true` is a
// whole-shop holiday closure and short-circuits levels 2 and 4. A resource-specific
// shift (level 1) still beats it. A business shift with `closed: false` does not
// short-circuit.
//
// This file mirrors functions/src/utils/effectiveHours.ts — the two must be kept in
// sync by hand, the same arrangement businessType.helper.js already uses. The
// functions copy is the tested one (functions/src/utils/test/), since `app/` has no
// test runner.

export const DAYS_IN_WEEK = 7;

export const DEFAULT_DAY_HOURS = { begin: "00:00", end: "23:45", closed: false };

// --- Shift scope ---------------------------------------------------------------
// A shift with no `resourceId` applies to the whole business — that is every shift
// doc written before appointments existed. `resourceId` is omitted rather than
// nulled on write, so a falsy check is the correct test.

export const isBusinessWideShift = shift => !!shift && !shift.resourceId;

export const isShiftForResource = (shift, resourceId) => !!shift && !!resourceId && shift.resourceId === resourceId;

export const filterBusinessWideShifts = (shifts = []) => (shifts || []).filter(isBusinessWideShift);

export const selectBusinessWideShift = (shifts = []) => (shifts || []).find(isBusinessWideShift);

export const selectResourceShift = (shifts = [], resourceId) => {
  if (!resourceId) {
    return undefined;
  }
  return (shifts || []).find(shift => isShiftForResource(shift, resourceId));
};

// --- Weekly schedules ----------------------------------------------------------

// Luxon reports Sunday as 7; every hours array in this codebase indexes it as 0.
export const toDayIndex = luxonWeekday => (luxonWeekday === 7 ? 0 : luxonWeekday);

export const hasCustomSchedule = resource => Array.isArray(resource?.schedule) && resource.schedule.length > 0;

// Always returns exactly 7 freshly-cloned rows, so callers can edit a row without
// mutating the array they seeded from.
export const normalizeWeeklySchedule = (schedule, fallbackDay = DEFAULT_DAY_HOURS) => {
  const rows = [];
  for (let index = 0; index < DAYS_IN_WEEK; index++) {
    const row = Array.isArray(schedule) ? schedule[index] : undefined;
    rows.push(row ? { ...row } : { ...fallbackDay });
  }
  return rows;
};

// Splits the editor's state into the document to write and the fields to remove.
// "Use business hours" has to *delete* `schedule`: under `{ merge: true }` simply
// omitting it would leave the previous array live.
export const buildResourceSchedulePayload = (useCustomSchedule, rows) => {
  if (!useCustomSchedule || !Array.isArray(rows) || !rows.length) {
    return { data: {}, clearedFields: ["schedule"] };
  }
  return { data: { schedule: normalizeWeeklySchedule(rows) }, clearedFields: [] };
};

// --- Resolution ----------------------------------------------------------------

const fromShift = (shift, source) => {
  if (shift.closed) {
    return { closed: true, begin: null, end: null, source };
  }
  return { closed: false, begin: shift.begin ?? null, end: shift.end ?? null, source };
};

const fromDayHours = (day, source) => {
  // `closed` is absent on an open day in `settings.reservations.hours`.
  if (day.closed) {
    return { closed: true, begin: null, end: null, source };
  }
  return { closed: false, begin: day.begin ?? null, end: day.end ?? null, source };
};

export const resolveEffectiveHours = ({ dayIndex, resourceShift, resourceSchedule, businessShift, businessHours }) => {
  if (resourceShift) {
    return fromShift(resourceShift, "resourceShift");
  }

  // Whole-shop closure beats a resource's weekly schedule — see the header note.
  if (businessShift?.closed) {
    return { closed: true, begin: null, end: null, source: "businessClosure" };
  }

  const scheduledDay = Array.isArray(resourceSchedule) ? resourceSchedule[dayIndex] : undefined;
  if (scheduledDay) {
    return fromDayHours(scheduledDay, "resourceSchedule");
  }

  if (businessShift) {
    return fromShift(businessShift, "businessShift");
  }

  const businessDay = Array.isArray(businessHours) ? businessHours[dayIndex] : undefined;
  if (businessDay) {
    return fromDayHours(businessDay, "businessHours");
  }

  // Nothing configured at all. Fail closed rather than inventing a window.
  return { closed: true, begin: null, end: null, source: "none" };
};

// Convenience wrapper over a single date's shift docs. Passing no `resourceId`
// reproduces the business-wide behaviour that predates appointments.
export const resolveEffectiveHoursForDate = ({ dayIndex, shifts, resourceId, resourceSchedule, businessHours }) =>
  resolveEffectiveHours({
    dayIndex,
    resourceShift: selectResourceShift(shifts, resourceId),
    resourceSchedule,
    businessShift: selectBusinessWideShift(shifts),
    businessHours
  });
